import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import {
  adminBlocks,
  adminLessons,
  adminSections,
  uploadAdminAudio,
  type BlockCreate,
  type LessonCreate,
  type SectionCreate,
} from "../adminApi";
import type { AdminBlock, AdminLesson, AdminSection, BlockItem } from "../types";
import { haptic, showConfirm, useMainButton } from "../tg";
import { NumberInput } from "../components/NumberInput";
import { ParagraphEditor } from "../components/ParagraphEditor";

// ============================================================================
// Общие мелочи
// ============================================================================

// inline-SVG иконки — без зависимостей, без эмодзи. Размер задаётся CSS-классом.
function IconEdit() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 20h9" strokeLinecap="round" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 11v6M14 11v6" strokeLinecap="round" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" strokeLinejoin="round" />
    </svg>
  );
}

function IconUp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 15 12 9 18 15" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 12 15 18 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconGear() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.16.69.4.99.69.36.36.85.66 1.42.66H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Тост: простой ephemeral state в каждом экране.
function useToast(): [string | null, (msg: string) => void] {
  const [toast, setToast] = useState<string | null>(null);
  const show = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  };
  return [toast, show];
}

function ToastBar({ message }: { message: string | null }) {
  if (!message) return null;
  return <div className="admin-toast">{message}</div>;
}

// Reorder helper: меняет местами idx с соседом.
function swap<T>(arr: T[], i: number, j: number): T[] {
  if (i < 0 || j < 0 || i >= arr.length || j >= arr.length) return arr;
  const next = arr.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ============================================================================
// AdminSections — корневой экран админки
// ============================================================================

type AdminSectionsProps = {
  // Если кеш есть в App — рендер мгновенный, без "Загрузка…". Свежий запрос всё равно
  // полетит в useEffect и обновит данные, когда придёт ответ.
  initialSections?: AdminSection[];
  onSectionsLoaded?: (sections: AdminSection[]) => void;
  onOpenSection: (section: AdminSection) => void;
  // existingCount пробрасываем чтобы редактор «новой» секции получил разумный display_order.
  onEditSection: (section: AdminSection | null, existingCount: number) => void;
  onOpenUITest: () => void;
};

export function AdminSections({
  initialSections,
  onSectionsLoaded,
  onOpenSection,
  onEditSection,
  onOpenUITest,
}: AdminSectionsProps) {
  const [items, setItems] = useState<AdminSection[] | null>(initialSections ?? null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();

  useMainButton(null);

  const reload = async () => {
    try {
      const data = await adminSections.list();
      setItems(data);
      onSectionsLoaded?.(data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    reload();
    // reload стабилен по поведению — линту достаточно одного запуска на mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moveItem = async (idx: number, dir: -1 | 1) => {
    if (!items || busy) return;
    const next = swap(items, idx, idx + dir);
    if (next === items) return;
    setItems(next); // оптимистично
    setBusy(true);
    try {
      const updated = await adminSections.reorder(next.map((s) => s.id));
      setItems(updated);
      onSectionsLoaded?.(updated);
    } catch (e) {
      setItems(items); // откат
      showToast(`Не удалось переставить: ${(e as Error).message}`);
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>Админка</h1>
        <p className="subtitle">Секции / Уроки / Блоки</p>
      </header>

      <ToastBar message={toast} />

      <div className="admin-list">
        <div className="admin-row">
          <button
            type="button"
            className="admin-row-main"
            onClick={onOpenUITest}
          >
            <div className="admin-row-title">Тест UI</div>
            <div className="admin-row-meta">
              <span className="admin-pill admin-pill-soft">песочница</span>
              <span>сравнить варианты рендера блока</span>
            </div>
          </button>
        </div>
      </div>

      {error && !items && <div className="error-card">{error}</div>}
      {!items && !error && <div className="card">Загрузка…</div>}

      {items && (
        <div className="admin-list">
          {items.map((sec, idx) => (
            <div key={sec.id} className="admin-row">
              <button
                type="button"
                className="admin-row-main"
                onClick={() => onOpenSection(sec)}
              >
                <div className="admin-row-title">{sec.title}</div>
                <div className="admin-row-meta">
                  <span className="admin-pill">#{sec.display_order}</span>
                  <span className="admin-slug">{sec.id}</span>
                  <span>{sec.lessons_total} ур.</span>
                </div>
                <div className="admin-row-status">
                  <span className={sec.available ? "ok" : "off"}>
                    {sec.available ? "доступна" : "скрыта"}
                  </span>
                </div>
              </button>
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0 || busy}
                  aria-label="Выше"
                >
                  <IconUp />
                </button>
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1 || busy}
                  aria-label="Ниже"
                >
                  <IconDown />
                </button>
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => onEditSection(sec, items.length)}
                  disabled={busy}
                  aria-label="Изменить"
                >
                  <IconEdit />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AdminSectionEdit — создание/правка секции
// ============================================================================

type AdminSectionEditProps = {
  initial: AdminSection | null;
  // existing нужен чтобы выставить разумный display_order для новой секции
  existingCount: number;
  onSaved: () => void;
};

export function AdminSectionEdit({
  initial,
  existingCount,
  onSaved,
}: AdminSectionEditProps) {
  const isNew = initial === null;
  const [id, setId] = useState(initial?.id ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [available, setAvailable] = useState(initial?.available ?? false);
  const [reason, setReason] = useState(initial?.available_reason ?? "");
  const [maxPerDay, setMaxPerDay] = useState(initial?.max_per_day ?? 1);
  const [order, setOrder] = useState(initial?.display_order ?? existingCount + 1);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const save = async () => {
    if (saving) return;
    if (isNew && !id.trim()) {
      showToast("Укажите id (slug) секции");
      return;
    }
    if (!title.trim()) {
      showToast("Укажите название");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const body: SectionCreate = {
          id: id.trim(),
          title: title.trim(),
          available,
          available_reason: reason.trim() ? reason.trim() : null,
          max_per_day: maxPerDay,
          display_order: order,
        };
        await adminSections.create(body);
      } else {
        await adminSections.update(initial!.id, {
          title: title.trim(),
          available,
          available_reason: reason.trim() ? reason.trim() : null,
          max_per_day: maxPerDay,
          display_order: order,
        });
      }
      haptic("success");
      onSaved();
    } catch (e) {
      haptic("error");
      showToast((e as Error).message);
      setSaving(false);
    }
  };

  // MainButton в админских формах не используем — на iOS он прыгает за клавиатурой
  // при focus на input/textarea. Кнопка «Сохранить» — inline ниже формы.
  useMainButton(null);

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{isNew ? "Новая секция" : "Изменить секцию"}</h1>
      </header>

      <ToastBar message={toast} />

      <div className="admin-form">
        <label className="admin-field">
          <span>ID (slug)</span>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            disabled={!isNew}
            placeholder="lessons"
          />
        </label>

        <label className="admin-field">
          <span>Название</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Уроки"
          />
        </label>

        <label className="admin-field admin-field-row">
          <input
            type="checkbox"
            checked={available}
            onChange={(e) => setAvailable(e.target.checked)}
          />
          <span>Доступна пользователям</span>
        </label>

        <label className="admin-field">
          <span>Подпись «недоступна» (опционально)</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Скоро откроется"
          />
        </label>

        <label className="admin-field">
          <span>Макс. в день</span>
          <NumberInput
            value={maxPerDay}
            onChange={setMaxPerDay}
            min={1}
            step={1}
          />
        </label>

        <label className="admin-field">
          <span>Display order</span>
          <NumberInput
            value={order}
            onChange={setOrder}
            step={1}
          />
        </label>

        <button
          type="button"
          className="admin-save-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Сохранение…" : isNew ? "Создать" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// AdminLessons — уроки секции
// ============================================================================

type AdminLessonsProps = {
  section: AdminSection;
  onOpenLesson: (lesson: AdminLesson) => void;
  onEditLesson: (lesson: AdminLesson | null, existingCount: number) => void;
};

export function AdminLessons({
  section,
  onOpenLesson,
  onEditLesson,
}: AdminLessonsProps) {
  const [items, setItems] = useState<AdminLesson[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();

  useMainButton(null);

  const reload = async () => {
    try {
      const data = await adminLessons.list(section.id);
      setItems(data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    reload();
  }, [section.id]);

  const moveItem = async (idx: number, dir: -1 | 1) => {
    if (!items || busy) return;
    const next = swap(items, idx, idx + dir);
    if (next === items) return;
    setItems(next);
    setBusy(true);
    try {
      const updated = await adminLessons.reorder(section.id, next.map((l) => l.id));
      setItems(updated);
    } catch (e) {
      setItems(items);
      showToast(`Не удалось переставить: ${(e as Error).message}`);
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (lesson: AdminLesson) => {
    if (!window.confirm(`Удалить урок "${lesson.title}"? Удалятся все блоки.`)) return;
    setBusy(true);
    try {
      await adminLessons.remove(lesson.id);
      haptic("success");
      setItems((cur) => (cur ? cur.filter((l) => l.id !== lesson.id) : cur));
    } catch (e) {
      showToast(`Ошибка: ${(e as Error).message}`);
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>Уроки</h1>
        <p className="subtitle">{section.title}</p>
      </header>

      <ToastBar message={toast} />

      {error && !items && <div className="error-card">{error}</div>}
      {!items && !error && <div className="card">Загрузка…</div>}

      {items && items.length === 0 && (
        <div className="card">Уроков пока нет.</div>
      )}

      {items && items.length > 0 && (
        <div className="admin-list">
          {items.map((lesson, idx) => (
            <div key={lesson.id} className="admin-row">
              <button
                type="button"
                className="admin-row-main"
                onClick={() => onOpenLesson(lesson)}
              >
                <div className="admin-row-title">{lesson.title}</div>
                <div className="admin-row-meta">
                  <span className="admin-pill">#{lesson.display_order}</span>
                  <span>{lesson.blocks_count} бл.</span>
                </div>
              </button>
              <div className="admin-actions">
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0 || busy}
                  aria-label="Выше"
                >
                  <IconUp />
                </button>
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1 || busy}
                  aria-label="Ниже"
                >
                  <IconDown />
                </button>
                <button
                  type="button"
                  className="admin-iconbtn"
                  onClick={() => onEditLesson(lesson, items.length)}
                  disabled={busy}
                  aria-label="Изменить"
                >
                  <IconEdit />
                </button>
                <button
                  type="button"
                  className="admin-iconbtn admin-iconbtn-danger"
                  onClick={() => remove(lesson)}
                  disabled={busy}
                  aria-label="Удалить"
                >
                  <IconTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="admin-add"
        onClick={() => onEditLesson(null, items?.length ?? 0)}
      >
        + Добавить урок
      </button>
    </div>
  );
}

// ============================================================================
// AdminLessonEdit
// ============================================================================

type AdminLessonEditProps = {
  section: AdminSection;
  initial: AdminLesson | null;
  existingCount: number;
  onSaved: () => void;
};

export function AdminLessonEdit({
  section,
  initial,
  existingCount,
  onSaved,
}: AdminLessonEditProps) {
  const isNew = initial === null;
  const [title, setTitle] = useState(initial?.title ?? "");
  const [order, setOrder] = useState(initial?.display_order ?? existingCount + 1);
  const [saving, setSaving] = useState(false);
  const [toast, showToast] = useToast();

  const save = async () => {
    if (saving) return;
    if (!title.trim()) {
      showToast("Укажите название");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const body: LessonCreate = {
          title: title.trim(),
          display_order: order,
        };
        await adminLessons.create(section.id, body);
      } else {
        await adminLessons.update(initial!.id, {
          title: title.trim(),
          display_order: order,
        });
      }
      haptic("success");
      onSaved();
    } catch (e) {
      haptic("error");
      showToast((e as Error).message);
      setSaving(false);
    }
  };

  // MainButton в админских формах не используем — на iOS он прыгает за клавиатурой
  // при focus на input/textarea. Кнопка «Сохранить» — inline ниже формы.
  useMainButton(null);

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{isNew ? "Новый урок" : "Изменить урок"}</h1>
        <p className="subtitle">{section.title}</p>
      </header>

      <ToastBar message={toast} />

      <div className="admin-form">
        <label className="admin-field">
          <span>Название</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например, «короче говоря»"
          />
        </label>

        <label className="admin-field">
          <span>Display order</span>
          <NumberInput
            value={order}
            onChange={setOrder}
            step={1}
          />
        </label>

        <button
          type="button"
          className="admin-save-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Сохранение…" : isNew ? "Создать" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// AdminBlocks — блоки урока
// ============================================================================

type AdminBlocksProps = {
  lesson: AdminLesson;
  onEditBlock: (block: AdminBlock | null, existingCount: number) => void;
};

export function AdminBlocks({ lesson, onEditBlock }: AdminBlocksProps) {
  const [items, setItems] = useState<AdminBlock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, showToast] = useToast();

  useMainButton(null);

  const reload = async () => {
    try {
      const data = await adminBlocks.list(lesson.id);
      setItems(data);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    reload();
  }, [lesson.id]);

  const moveItem = async (idx: number, dir: -1 | 1) => {
    if (!items || busy) return;
    const next = swap(items, idx, idx + dir);
    if (next === items) return;
    setItems(next);
    setBusy(true);
    try {
      const updated = await adminBlocks.reorder(lesson.id, next.map((b) => b.id));
      setItems(updated);
    } catch (e) {
      setItems(items);
      showToast(`Не удалось переставить: ${(e as Error).message}`);
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (block: AdminBlock) => {
    if (!window.confirm(`Удалить "${block.title || "блок без названия"}"?`)) return;
    setBusy(true);
    try {
      await adminBlocks.remove(block.id);
      haptic("success");
      setItems((cur) => (cur ? cur.filter((b) => b.id !== block.id) : cur));
    } catch (e) {
      showToast(`Ошибка: ${(e as Error).message}`);
      haptic("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>Блоки</h1>
        <p className="subtitle">{lesson.title}</p>
      </header>

      <ToastBar message={toast} />

      {error && !items && <div className="error-card">{error}</div>}
      {!items && !error && <div className="card">Загрузка…</div>}

      {items && items.length === 0 && (
        <div className="card">Блоков пока нет.</div>
      )}

      {items && items.length > 0 && (
        <div className="admin-list">
          {items.map((block, idx) => {
            // Превью — первый paragraph.
            const firstPara = block.items.find((it) => it.type === "paragraph");
            const paraText = firstPara && firstPara.type === "paragraph" ? firstPara.text : "";
            const preview = paraText.slice(0, 80).replace(/\s+/g, " ");
            // Сводка по типам — что есть в блоке.
            const counts = block.items.reduce<Record<string, number>>((acc, it) => {
              acc[it.type] = (acc[it.type] ?? 0) + 1;
              return acc;
            }, {});
            const summary = (["paragraph", "audio", "video", "image", "exercise"] as const)
              .filter((t) => counts[t])
              .map((t) => {
                const labels = {
                  paragraph: "текст",
                  audio: "аудио",
                  video: "видео",
                  image: "фото",
                  exercise: "задание",
                } as const;
                return counts[t] > 1 ? `${labels[t]}×${counts[t]}` : labels[t];
              })
              .join(" · ");
            return (
              <div key={block.id} className="admin-row">
                <button
                  type="button"
                  className="admin-row-main"
                  onClick={() => onEditBlock(block, items.length)}
                >
                  <div className="admin-row-title">
                    {block.title || <em className="admin-untitled">без заголовка</em>}
                  </div>
                  <div className="admin-row-meta">
                    <span className="admin-pill">#{block.display_order}</span>
                    <span className="admin-pill admin-pill-soft">{block.button_caption}</span>
                    {summary && <span className="admin-pill admin-pill-soft">{summary}</span>}
                  </div>
                  {preview && <div className="admin-row-preview">{preview}{paraText.length > 80 ? "…" : ""}</div>}
                </button>
                <div className="admin-actions">
                  <button
                    type="button"
                    className="admin-iconbtn"
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0 || busy}
                    aria-label="Выше"
                  >
                    <IconUp />
                  </button>
                  <button
                    type="button"
                    className="admin-iconbtn"
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1 || busy}
                    aria-label="Ниже"
                  >
                    <IconDown />
                  </button>
                  <button
                    type="button"
                    className="admin-iconbtn admin-iconbtn-danger"
                    onClick={() => remove(block)}
                    disabled={busy}
                    aria-label="Удалить"
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        className="admin-add"
        onClick={() => onEditBlock(null, items?.length ?? 0)}
      >
        + Добавить блок
      </button>
    </div>
  );
}

// ============================================================================
// AdminBlockEdit — редактор блока с markdown-превью
// ============================================================================

type AdminBlockEditProps = {
  lesson: AdminLesson;
  initial: AdminBlock | null;
  existingCount: number;
  // Вызывается после ручного [Сохранить] — App инвалидирует кеш и закрывает форму.
  onSaved: () => void;
  // Вызывается после каждого успешного autosave — чтобы App инвалидировал кеш
  // блоков (юзер ушёл без нажатия [Сохранить], но данные на бэке уже свежие).
  onAutosaved?: () => void;
  // Колбэк для регистрации guard'а на BackButton: пока в форме есть несохранённые
  // изменения — App спросит подтверждение перед уходом. null = снять guard.
  registerUnsavedGuard?: (g: (() => Promise<boolean>) | null) => void;
};

// Пустой item каждого типа — для свежевставленных компонентов.
function emptyItem(type: BlockItem["type"]): BlockItem {
  switch (type) {
    case "paragraph":
      return { type: "paragraph", text: "" };
    case "audio":
      return { type: "audio", url: "", title: null };
    case "video":
      return { type: "video", url: "", title: null };
    case "image":
      return { type: "image", url: "" };
    case "exercise":
      return {
        type: "exercise",
        title: "",
        description_md: "",
        repetitions_per_day: 1,
        timer_seconds: null,
        metronome_bpm: null,
      };
    case "user_video_record":
      return {
        type: "user_video_record",
        title: null,
        caption: null,
        max_recordings: 1,
      };
  }
}

// Подпись типа в UI редактора.
const ITEM_LABELS: Record<BlockItem["type"], string> = {
  paragraph: "Текст",
  audio: "Аудио",
  video: "Видео",
  image: "Фото",
  exercise: "Задание",
  user_video_record: "Запись",
};

// Снимок body для initial-блока — нужен, чтобы при открытии формы существующего
// блока isDirty начинался с false. Структура должна совпадать с тем, что
// возвращает buildBody() внутри AdminBlockEdit: те же поля, та же нормализация.
function buildInitialBody(initial: AdminBlock): BlockCreate {
  return {
    title: initial.title,
    button_caption: initial.button_caption,
    display_order: initial.display_order,
    items: initial.items,
    dz_required: initial.dz_required,
  };
}

// Цвета вынесены в один словарь — никаких раскиданных по jsx стилей.
const AUTOSAVE_COLORS = {
  saved: "#4caf50",
  dirty: "#e0a13a",
  saving: "#6ab3f3",
  error: "#f08080",
} as const;

function formatSavedAgo(at: number, now: number): string {
  const sec = Math.max(0, Math.floor((now - at) / 1000));
  if (sec < 5) return "Сохранено • только что";
  if (sec < 60) return `Сохранено • ${sec} сек назад`;
  const min = Math.floor(sec / 60);
  return `Сохранено • ${min} мин назад`;
}

function AutosaveBadge({
  status,
  isDirty,
  nowTick,
  onRetry,
}: {
  status: AutosaveStatus;
  isDirty: boolean;
  nowTick: number;
  onRetry: () => void;
}) {
  // Видимый статус выбирается из (status, isDirty). isDirty имеет приоритет над
  // saved/idle: если юзер что-то изменил после сохранения — показываем dirty,
  // не вводя пользователя в заблуждение «всё сохранено».
  if (status.kind === "saving") {
    return (
      <div className="admin-autosave" style={{ color: AUTOSAVE_COLORS.saving }}>
        Сохранение…
      </div>
    );
  }
  if (status.kind === "error") {
    return (
      <div className="admin-autosave" style={{ color: AUTOSAVE_COLORS.error }}>
        Не удалось сохранить — проверьте сеть{" "}
        <button
          type="button"
          className="admin-autosave-retry"
          onClick={onRetry}
        >
          Повторить
        </button>
      </div>
    );
  }
  if (isDirty) {
    return (
      <div className="admin-autosave" style={{ color: AUTOSAVE_COLORS.dirty }}>
        Несохранённые изменения
      </div>
    );
  }
  if (status.kind === "saved") {
    return (
      <div className="admin-autosave" style={{ color: AUTOSAVE_COLORS.saved }}>
        {formatSavedAgo(status.at, nowTick)}
      </div>
    );
  }
  // idle и нет dirty — ничего не показываем (форма ровно как открыли).
  return null;
}

// Статус autosave в шапке формы. Один из:
// - idle:    нет несохранённых изменений (либо ничего не вводилось)
// - dirty:   есть изменения, debounce ждёт окно тишины
// - saving:  PATCH/POST в полёте
// - saved:   только что сохранено (включая «N сек назад»)
// - error:   серия ретраев исчерпана, ждём действие пользователя
type AutosaveStatus =
  | { kind: "idle" }
  | { kind: "dirty" }
  | { kind: "saving" }
  | { kind: "saved"; at: number }
  | { kind: "error"; message: string };

export function AdminBlockEdit({
  lesson,
  initial,
  existingCount,
  onSaved,
  onAutosaved,
  registerUnsavedGuard,
}: AdminBlockEditProps) {
  // createdId хранит id блока, созданного autosave'ом из режима "новый блок".
  // После первого успешного POST дальше идут PATCH'и по этому id.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const isNew = initial === null && createdId === null;
  const [title, setTitle] = useState(initial?.title ?? "");
  const [items, setItems] = useState<BlockItem[]>(initial?.items ?? []);
  const [buttonCaption, setButtonCaption] = useState(initial?.button_caption ?? "Дальше");
  const [order, setOrder] = useState(initial?.display_order ?? existingCount + 1);
  const [dzRequired, setDzRequired] = useState(initial?.dz_required ?? false);
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  // Загрузка медиа — индекс item'а, чтобы блокировать только его на время upload.
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [toast, showToast] = useToast();
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>({
    kind: "idle",
  });
  // Тик раз в секунду для отображения «N сек назад» в статусе.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (autosaveStatus.kind !== "saved") return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [autosaveStatus.kind]);

  // Чекбокс «Дневные» виден только если в блоке есть хотя бы один exercise item.
  // Иначе показываем подсказку — exercise можно добавить и блок попадёт в Дневные.
  const hasExercise = items.some((it) => it.type === "exercise");
  // file inputs создаём лениво по индексу — но проще ref на скрытый input + state с pending index.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadIdxRef = useRef<number | null>(null);

  const busy = saving || uploadingIdx !== null;

  // --- Item helpers ---------------------------------------------------------
  const updateItem = (idx: number, patch: Partial<BlockItem>) => {
    setItems((cur) =>
      cur.map((it, i) => (i === idx ? ({ ...it, ...patch } as BlockItem) : it)),
    );
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    setItems((cur) => swap(cur, idx, j));
  };

  const removeItem = (idx: number) => {
    if (!window.confirm("Удалить компонент?")) return;
    setItems((cur) => cur.filter((_, i) => i !== idx));
    if (previewIdx === idx) setPreviewIdx(null);
  };

  const addItem = (type: BlockItem["type"]) => {
    setItems((cur) => [...cur, emptyItem(type)]);
    setShowTypePicker(false);
  };

  // --- Upload (аудио/видео) -------------------------------------------------
  const triggerUpload = (idx: number) => {
    if (busy) return;
    const item = items[idx];
    if (!item || (item.type !== "audio" && item.type !== "video")) return;
    pendingUploadIdxRef.current = idx;
    // accept выставим перед кликом — чтобы фильтр был релевантен типу.
    if (fileInputRef.current) {
      fileInputRef.current.accept =
        item.type === "audio"
          ? "audio/*,.mp3,.m4a,.ogg,.oga,.opus,.wav,.aac"
          : "video/*,.mp4,.mov,.webm,.m4v,image/gif,.gif";
      fileInputRef.current.click();
    }
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const idx = pendingUploadIdxRef.current;
    pendingUploadIdxRef.current = null;
    if (!file || idx === null) return;
    const item = items[idx];
    if (!item || (item.type !== "audio" && item.type !== "video")) return;
    setUploadingIdx(idx);
    try {
      // Используем тот же endpoint /admin/upload — он не различает audio/video,
      // лимит общий (audio_upload_max_bytes). Для видео имеет смысл проверить
      // server-side лимит на бэке позже, но MVP так.
      const res = await uploadAdminAudio(file);
      // Размеры и длительность бэк возвращает только если ffprobe смог их извлечь.
      // Для аудио — только duration_seconds, width/height не имеют смысла.
      const patch: Partial<BlockItem> = { url: res.url };
      if (item.type === "video") {
        if (res.width != null) (patch as any).width = res.width;
        if (res.height != null) (patch as any).height = res.height;
        if (res.is_gif === true) (patch as any).is_gif = true;
      }
      if (res.duration_seconds != null) {
        (patch as any).duration_seconds = res.duration_seconds;
      }
      updateItem(idx, patch);
      haptic("success");
      showToast("Загружено");
    } catch (err) {
      haptic("error");
      showToast(`Ошибка загрузки: ${(err as Error).message}`);
    } finally {
      setUploadingIdx(null);
    }
  };

  // --- Валидация и сборка body ----------------------------------------------
  // Возвращает null если форма валидна, иначе текст ошибки. Используется и в
  // ручном save (показываем toast), и в autosave (тогда просто не шлём запрос).
  const validate = (): string | null => {
    if (!title.trim()) return "Заголовок блока обязателен";
    if (!buttonCaption.trim()) return "Подпись кнопки не может быть пустой";
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type === "paragraph" && !it.text.trim()) {
        return `Компонент ${i + 1}: текст пустой`;
      }
      if ((it.type === "audio" || it.type === "video") && !it.url.trim()) {
        return `Компонент ${i + 1}: URL пустой`;
      }
      if (it.type === "image" && !it.url.trim()) {
        return `Компонент ${i + 1}: URL пустой`;
      }
      if (it.type === "exercise") {
        if (!it.title.trim()) return `Компонент ${i + 1}: название задания пустое`;
        if (
          it.metronome_bpm !== null &&
          (it.metronome_bpm < 20 || it.metronome_bpm > 300)
        ) {
          return `Компонент ${i + 1}: BPM должен быть 20–300`;
        }
        if (it.timer_seconds !== null && it.timer_seconds < 1) {
          return `Компонент ${i + 1}: таймер должен быть ≥ 1 сек`;
        }
        if (it.repetitions_per_day < 1 || it.repetitions_per_day > 100) {
          return `Компонент ${i + 1}: повторений должно быть 1–100`;
        }
      }
      if (it.type === "user_video_record") {
        if (it.max_recordings < 1 || it.max_recordings > 10) {
          return `Компонент ${i + 1}: количество видео должно быть 1–10`;
        }
      }
    }
    return null;
  };

  const buildBody = (): BlockCreate => {
    const normalized: BlockItem[] = items.map((it) => {
      if (it.type === "paragraph") return { type: "paragraph", text: it.text.trim() };
      if (it.type === "audio")
        return {
          type: "audio",
          url: it.url.trim(),
          title: it.title?.trim() ? it.title.trim() : null,
          duration_seconds: it.duration_seconds ?? null,
          caption: it.caption?.trim() ? it.caption.trim() : null,
        };
      if (it.type === "video")
        return {
          type: "video",
          url: it.url.trim(),
          title: it.title?.trim() ? it.title.trim() : null,
          width: it.width ?? null,
          height: it.height ?? null,
          duration_seconds: it.duration_seconds ?? null,
          caption: it.caption?.trim() ? it.caption.trim() : null,
        };
      if (it.type === "image")
        return {
          type: "image",
          url: it.url.trim(),
          caption: it.caption?.trim() ? it.caption.trim() : null,
        };
      if (it.type === "user_video_record")
        return {
          type: "user_video_record",
          title: it.title?.trim() ? it.title.trim() : null,
          caption: it.caption?.trim() ? it.caption.trim() : null,
          max_recordings: it.max_recordings,
        };
      return {
        type: "exercise",
        title: it.title.trim(),
        description_md: it.description_md.trim(),
        repetitions_per_day: it.repetitions_per_day,
        timer_seconds: it.timer_seconds,
        metronome_bpm: it.metronome_bpm,
      };
    });
    // Если упражнений нет — dz_required не имеет смысла, бэк его всё равно сбросит.
    const effectiveDzRequired = hasExercise && dzRequired;
    return {
      title: title.trim(),
      button_caption: buttonCaption.trim(),
      display_order: order,
      items: normalized,
      dz_required: effectiveDzRequired,
    };
  };

  // --- Save (ручной — кнопка [Сохранить]) -----------------------------------
  const save = async () => {
    if (busy) return;
    const err = validate();
    if (err) {
      showToast(err);
      return;
    }
    setSaving(true);
    try {
      const body = buildBody();
      const targetId = initial?.id ?? createdId;
      if (targetId) {
        await adminBlocks.update(targetId, body);
      } else {
        await adminBlocks.create(lesson.id, body);
      }
      haptic("success");
      // Снимаем guard перед навигацией — переход чистый.
      savedSnapshotRef.current = JSON.stringify(body);
      registerUnsavedGuard?.(null);
      onSaved();
    } catch (e) {
      haptic("error");
      showToast((e as Error).message);
      setSaving(false);
    }
  };

  // --- Autosave -------------------------------------------------------------
  // savedSnapshot: JSON последнего успешно отправленного body (после POST или PATCH).
  // Если текущий buildBody() === snapshot — форма «синхронизирована», dirty=false.
  // Инициализация: для существующего блока — снимок исходного состояния (initial),
  // для нового — пустая строка (любое заполнение = dirty).
  const savedSnapshotRef = useRef<string>(
    initial ? JSON.stringify(buildInitialBody(initial)) : "",
  );
  // Текущий снимок (зависит от title/items/...) — пересчитываем каждый render.
  const currentBody = buildBody();
  const currentSnapshot = JSON.stringify(currentBody);
  const isDirty = currentSnapshot !== savedSnapshotRef.current;

  // Таймер debounce и счётчик ретраев — в ref, чтобы не плодить перерендеры.
  const autosaveTimerRef = useRef<number | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const retryCountRef = useRef<number>(0);
  // Защита от race: если стартанул новый autosave пока летит старый — не дублируем.
  const inFlightRef = useRef<boolean>(false);

  const runAutosave = useCallback(async () => {
    if (inFlightRef.current) return;
    if (saving) return; // ручное сохранение в полёте — не мешаем
    const err = validate();
    if (err) {
      // Невалидно — оставляем dirty, бэку не шлём, но и ошибку не показываем
      // (юзер ещё печатает). Статус уже dirty за счёт сравнения снимков.
      return;
    }
    inFlightRef.current = true;
    setAutosaveStatus({ kind: "saving" });
    const body = buildBody();
    const snapshotAtSend = JSON.stringify(body);
    try {
      const targetId = initial?.id ?? createdId;
      if (targetId) {
        await adminBlocks.update(targetId, body);
      } else {
        const created = await adminBlocks.create(lesson.id, body);
        setCreatedId(created.id);
      }
      savedSnapshotRef.current = snapshotAtSend;
      retryCountRef.current = 0;
      onAutosaved?.();
      // Если за время запроса юзер успел напечатать ещё — статус сразу dirty,
      // следующий debounce-тик отправит свежий снимок.
      setAutosaveStatus((prev) =>
        prev.kind === "saving"
          ? { kind: "saved", at: Date.now() }
          : prev,
      );
    } catch (e) {
      retryCountRef.current += 1;
      if (retryCountRef.current <= 3) {
        // Ретрай через 3 сек. Статус — saving, чтобы UI оставался спокойным.
        setAutosaveStatus({ kind: "saving" });
        if (retryTimerRef.current !== null) {
          window.clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          inFlightRef.current = false;
          void runAutosave();
        }, 3000);
        return;
      }
      // Серия исчерпана — показываем ошибку, ждём действия юзера.
      setAutosaveStatus({
        kind: "error",
        message: (e as Error).message,
      });
    } finally {
      inFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdId, dzRequired, hasExercise, items, lesson.id, order, buttonCaption, saving, title, initial, onAutosaved]);

  // Debounce: при любом изменении формы — таймер на 2с, по истечении — autosave.
  useEffect(() => {
    if (!isDirty) return;
    // Если есть открытый таймер ретрая — не трогаем его (autosave сам перезапустится).
    if (retryTimerRef.current !== null) return;
    setAutosaveStatus((prev) =>
      prev.kind === "saving" || prev.kind === "error" ? prev : { kind: "dirty" },
    );
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void runAutosave();
    }, 2000);
    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [currentSnapshot, isDirty, runAutosave]);

  // Cleanup retry-таймер при размонтировании.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  // Регистрируем guard для BackButton. Уход с dirty/saving/error → confirm.
  useEffect(() => {
    if (!registerUnsavedGuard) return;
    const guardActive =
      isDirty ||
      autosaveStatus.kind === "saving" ||
      autosaveStatus.kind === "error";
    if (!guardActive) {
      registerUnsavedGuard(null);
      return;
    }
    registerUnsavedGuard(() =>
      showConfirm("Есть несохранённые изменения. Выйти?"),
    );
    return () => registerUnsavedGuard(null);
  }, [isDirty, autosaveStatus.kind, registerUnsavedGuard]);

  // beforeunload — для случая, когда Mini App открыт в браузерной вкладке
  // и юзер закрывает вкладку / обновляет страницу.
  useEffect(() => {
    const guardActive =
      isDirty ||
      autosaveStatus.kind === "saving" ||
      autosaveStatus.kind === "error";
    if (!guardActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Спецификация: установка returnValue показывает нативный confirm.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, autosaveStatus.kind]);

  // Ручной retry по кнопке в статусе ошибки.
  const retryNow = () => {
    retryCountRef.current = 0;
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    void runAutosave();
  };

  // MainButton в админских формах не используем — на iOS он прыгает за клавиатурой
  // при focus на input/textarea. Кнопка «Сохранить» — inline ниже формы.
  useMainButton(null);

  return (
    <div className="screen screen-with-sticky-header">
      <header className="lesson-header-sticky">
        <h1>{isNew ? "Новый блок" : "Изменить блок"}</h1>
        <p className="subtitle">{lesson.title}</p>
        <AutosaveBadge
          status={autosaveStatus}
          isDirty={isDirty}
          nowTick={nowTick}
          onRetry={retryNow}
        />
      </header>

      <ToastBar message={toast} />

      <div className="admin-form">
        {/* --- Заголовок блока (обязательно, закреплён сверху) --- */}
        <label className="admin-field">
          <span>Заголовок блока</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например, «Тренировка губ»"
          />
        </label>
        <p className="admin-field-hint">
          Показывается и в админке (списком), и в уроке/Дневных — над контентом
          блока.
        </p>

        {/* --- Items builder --- */}
        <div className="admin-items">
          {items.length === 0 && (
            <div className="admin-items-empty">
              Компонентов пока нет. Добавь первый ниже.
            </div>
          )}
          {items.map((item, idx) => (
            <div key={idx} className="admin-item">
              <div className="admin-item-header">
                <span className="admin-item-type">
                  #{idx + 1} · {ITEM_LABELS[item.type]}
                </span>
                <div className="admin-item-controls">
                  <button
                    type="button"
                    className="admin-iconbtn"
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0 || busy}
                    aria-label="Выше"
                  >
                    <IconUp />
                  </button>
                  <button
                    type="button"
                    className="admin-iconbtn"
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1 || busy}
                    aria-label="Ниже"
                  >
                    <IconDown />
                  </button>
                  {item.type === "paragraph" && (
                    <button
                      type="button"
                      className="admin-iconbtn"
                      aria-pressed={previewIdx === idx}
                      onClick={() =>
                        setPreviewIdx(previewIdx === idx ? null : idx)
                      }
                      title="Превью"
                    >
                      Превью
                    </button>
                  )}
                  <button
                    type="button"
                    className="admin-iconbtn admin-iconbtn-danger"
                    onClick={() => removeItem(idx)}
                    disabled={busy}
                    aria-label="Удалить"
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>

              {item.type === "paragraph" && (
                <>
                  <ParagraphEditor
                    value={item.text}
                    onChange={(text) => updateItem(idx, { text })}
                    placeholder="**Жирный**, _курсив_, списки, ссылки"
                  />
                  {previewIdx === idx && (
                    <div className="admin-preview">
                      <div className="admin-preview-label">Превью</div>
                      <div className="block-text">
                        <ReactMarkdown>{item.text || "_(пусто)_"}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                </>
              )}

              {(item.type === "audio" || item.type === "video") && (
                <>
                  <input
                    type="url"
                    value={item.url}
                    onChange={(e) => updateItem(idx, { url: e.target.value })}
                    placeholder={
                      item.type === "audio"
                        ? "https://demosfen.palettelab.ru/s3/audio/..."
                        : "https://demosfen.palettelab.ru/s3/video/..."
                    }
                    disabled={uploadingIdx === idx}
                  />
                  <input
                    type="text"
                    value={item.title ?? ""}
                    onChange={(e) =>
                      updateItem(idx, { title: e.target.value || null })
                    }
                    placeholder="Заголовок (опционально, мелким над плеером)"
                    style={{ marginTop: 6 }}
                  />
                  <textarea
                    rows={3}
                    value={item.caption ?? ""}
                    onChange={(e) =>
                      updateItem(idx, { caption: e.target.value || null })
                    }
                    placeholder="Подпись под плеером (опционально, длинный текст)"
                    style={{ marginTop: 6 }}
                  />
                  <div className="admin-audio-actions">
                    <button
                      type="button"
                      className="admin-toggle"
                      onClick={() => triggerUpload(idx)}
                      disabled={busy}
                    >
                      {uploadingIdx === idx ? "Загрузка…" : "Загрузить файл"}
                    </button>
                    {item.url && (
                      <button
                        type="button"
                        className="admin-toggle admin-toggle-danger"
                        onClick={() => updateItem(idx, { url: "" })}
                        disabled={busy}
                      >
                        × Убрать URL
                      </button>
                    )}
                  </div>
                  {item.type === "audio" && (
                    <p className="admin-field-hint">
                      Или запиши голосовое боту{" "}
                      <a
                        href="https://t.me/ipo_lk_demosfen_bot"
                        target="_blank"
                        rel="noreferrer"
                      >
                        @ipo_lk_demosfen_bot
                      </a>{" "}
                      — он вернёт URL.
                    </p>
                  )}
                </>
              )}

              {item.type === "image" && (
                <>
                  <input
                    type="url"
                    value={item.url}
                    onChange={(e) => updateItem(idx, { url: e.target.value })}
                    placeholder="https://demosfen.palettelab.ru/s3/image/..."
                  />
                  <textarea
                    rows={3}
                    value={item.caption ?? ""}
                    onChange={(e) =>
                      updateItem(idx, { caption: e.target.value || null })
                    }
                    placeholder="Подпись под картинкой (опционально)"
                    style={{ marginTop: 6 }}
                  />
                </>
              )}

              {item.type === "exercise" && (
                <>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateItem(idx, { title: e.target.value })}
                    placeholder="Название задания"
                  />
                  <textarea
                    rows={4}
                    value={item.description_md}
                    onChange={(e) =>
                      updateItem(idx, { description_md: e.target.value })
                    }
                    placeholder="Описание (Markdown)"
                    style={{ marginTop: 6 }}
                  />
                  <label className="admin-field">
                    <span>Повторений за период</span>
                    <NumberInput
                      value={item.repetitions_per_day}
                      onChange={(n) =>
                        updateItem(idx, { repetitions_per_day: n })
                      }
                      min={1}
                      max={100}
                      step={1}
                    />
                  </label>
                  {(() => {
                    const mode: "none" | "timer" | "metronome" =
                      item.timer_seconds !== null
                        ? "timer"
                        : item.metronome_bpm !== null
                          ? "metronome"
                          : "none";
                    return (
                      <>
                        <label className="admin-field">
                          <span>Режим</span>
                          <select
                            value={mode}
                            onChange={(e) => {
                              const v = e.target.value as "none" | "timer" | "metronome";
                              if (v === "none") {
                                updateItem(idx, { timer_seconds: null, metronome_bpm: null });
                              } else if (v === "timer") {
                                updateItem(idx, { timer_seconds: 60, metronome_bpm: null });
                              } else {
                                updateItem(idx, { timer_seconds: null, metronome_bpm: 60 });
                              }
                            }}
                          >
                            <option value="none">Без таймера и метронома</option>
                            <option value="timer">Таймер (секунды)</option>
                            <option value="metronome">Метроном (BPM)</option>
                          </select>
                        </label>
                        {mode === "timer" && (
                          <label className="admin-field">
                            <span>Таймер, секунды</span>
                            <NumberInput
                              value={item.timer_seconds ?? 60}
                              onChange={(n) =>
                                updateItem(idx, { timer_seconds: n })
                              }
                              min={1}
                              step={1}
                              placeholder="например, 60"
                            />
                          </label>
                        )}
                        {mode === "metronome" && (
                          <label className="admin-field">
                            <span>Метроном, BPM</span>
                            <NumberInput
                              value={item.metronome_bpm ?? 60}
                              onChange={(n) =>
                                updateItem(idx, { metronome_bpm: n })
                              }
                              min={20}
                              max={300}
                              step={10}
                              placeholder="20–300"
                            />
                          </label>
                        )}
                      </>
                    );
                  })()}
                </>
              )}

              {item.type === "user_video_record" && (
                <>
                  <input
                    type="text"
                    value={item.title ?? ""}
                    onChange={(e) =>
                      updateItem(idx, {
                        title: e.target.value === "" ? null : e.target.value,
                      })
                    }
                    placeholder="Заголовок (необязательно)"
                    style={{ marginTop: 6 }}
                  />
                  <textarea
                    rows={3}
                    value={item.caption ?? ""}
                    onChange={(e) =>
                      updateItem(idx, {
                        caption: e.target.value === "" ? null : e.target.value,
                      })
                    }
                    placeholder="Подпись над счётчиком (необязательно)"
                    style={{ marginTop: 6 }}
                  />
                  <label className="admin-field">
                    <span>Количество видео</span>
                    <NumberInput
                      value={item.max_recordings}
                      onChange={(n) =>
                        updateItem(idx, { max_recordings: n })
                      }
                      min={1}
                      max={10}
                      step={1}
                    />
                  </label>
                </>
              )}
            </div>
          ))}

          {/* Скрытый file input — общий, реальный target item хранится в ref. */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={onFileChange}
            style={{ display: "none" }}
          />

          {/* Type picker */}
          {showTypePicker ? (
            <div className="admin-type-picker">
              {(["paragraph", "audio", "video", "image", "exercise", "user_video_record"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  className="admin-type-btn"
                  onClick={() => addItem(t)}
                >
                  {ITEM_LABELS[t]}
                </button>
              ))}
              <button
                type="button"
                className="admin-type-btn admin-type-btn-cancel"
                onClick={() => setShowTypePicker(false)}
              >
                Отмена
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="admin-add"
              onClick={() => setShowTypePicker(true)}
              disabled={busy}
            >
              + Добавить компонент
            </button>
          )}
        </div>

        {/* --- Block-level поля --- */}
        {hasExercise ? (
          <div className="admin-field">
            <label className="admin-field-checkbox">
              <input
                type="checkbox"
                checked={dzRequired}
                onChange={(e) => setDzRequired(e.target.checked)}
              />
              <span>Дневные</span>
            </label>
          </div>
        ) : (
          <div className="admin-field">
            <p className="admin-field-hint">
              Доступно после добавления упражнения.
            </p>
          </div>
        )}

        <label className="admin-field">
          <span>Подпись кнопки</span>
          <input
            type="text"
            value={buttonCaption}
            onChange={(e) => setButtonCaption(e.target.value)}
            placeholder="Дальше"
          />
        </label>

        <label className="admin-field">
          <span>Display order</span>
          <NumberInput
            value={order}
            onChange={setOrder}
            step={1}
          />
        </label>

        <button
          type="button"
          className="admin-save-btn"
          onClick={save}
          disabled={busy}
        >
          {saving ? "Сохранение…" : isNew ? "Создать" : "Сохранить и закрыть"}
        </button>
      </div>
    </div>
  );
}
